import React, { useState } from "react";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import IOnGetAccountResaleTaxDocReportDTO from "../../../../models/DTO/IOnGetAccountResaleTaxDocReportDTO";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import TitleTagEnum from "../../../../models/TitleTagEnum";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import IOwTableCols from "../../../../models/IOwTableCols";
import OwButton from "../../../shared/OwButton/OwButton";
import ExcelJS from "exceljs";

type Props = {
    processedReport: IProcessed_Report;
};

export const AccountResaleTaxDocumentReport = (props: Props) => {
    const [reportData, setReportData] = useState<IOnGetAccountResaleTaxDocReportDTO | undefined>(props.processedReport.report_data);
    const [showInactive, setShowInactive] = useState(false);

    const vendorsWithoutTaxDocs = reportData?.vendorsWithoutTaxDocs.filter((vendor) => (showInactive ? true : vendor.active)) || [];
    const vendorsWithTaxDocs = reportData?.vendorsWithTaxDocs.filter((vendor) => (showInactive ? true : vendor.active)) || [];

    const tableCols: IOwTableCols[] = [
        {
            title: "name",
            valueProp: "name",
        },
        {
            title: "rep",
            valueProp: "sales_representative_name",
        },
        {
            title: "active",
            valueProp: "active",
            renderFn: (value: boolean) => {
                return value ? `Active` : `Inactive`;
            },
            notVisible: !showInactive,
        },
    ];

    const tableColsTwo: IOwTableCols[] = [
        ...tableCols,
        {
            title: "resale_no",
            valueProp: "resale_tax_id",
        },
        {
            title: "resale_tax_doc",
            valueProp: "has_resale_tax_attachment",
            renderFn: (value: boolean) => {
                return value ? `Yes` : `No`;
            },
        },
    ];

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();

            const ws = workbook.addWorksheet(`Without Tax Docs (${vendorsWithoutTaxDocs.length})`);

            const headerCells = ["A1", "B1"];

            ws.columns = [
                { header: "Account", key: "account_name" },
                { header: "Rep", key: "sales_rep" },
            ];

            if (showInactive) {
                ws.columns.push({ header: "Active", key: "active" });

                headerCells.push("C1");
            }

            for (const header of headerCells) {
                ws.getCell(header).font = {
                    bold: true,
                };
            }

            for (const vendor of vendorsWithoutTaxDocs) {
                ws.addRow({
                    account_name: vendor.name,
                    sales_rep: vendor.sales_representative_name,
                    active: vendor.active ? "Active" : "Inactive",
                });
            }

            const wsTwo = workbook.addWorksheet(`With Tax Docs (${vendorsWithTaxDocs.length})`);

            wsTwo.columns = [
                { header: "Account", key: "account_name" },
                { header: "Rep", key: "sales_rep" },
                { header: "Resale No.", key: "resale_no" },
                { header: "Resale Tax Doc Uploaded", key: "resale_tax_doc" },
            ];

            if (showInactive) {
                wsTwo.columns.push({ header: "Active", key: "active" });
            }

            for (const header of headerCells) {
                wsTwo.getCell(header).font = {
                    bold: true,
                };
            }

            for (const vendor of vendorsWithTaxDocs) {
                wsTwo.addRow({
                    account_name: vendor.name,
                    sales_rep: vendor.sales_representative_name,
                    resale_no: vendor.resale_tax_id,
                    resale_tax_doc: vendor.has_resale_tax_attachment ? "Yes" : "No",
                    active: vendor.active ? "Active" : "Inactive",
                });
            }

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `Account Resale Tax Doc Report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"AccountResaleTaxDocumentReport"}>
            <OwTitle title={"account_resale_tax_document_report"} tag={"h3"} />
            <div className={"d-print-none d-flex"}>
                <div>
                    <OwButton className={"d-print-none btn-primary"} title={"excel"} fn={downloadExcel} leftIconClass={"fas fa-download"} />
                </div>
                <div className={"ms-2"}>
                    <OwToggle
                        fn={() => {
                            setShowInactive(!showInactive);
                        }}
                        name={"show_inactive"}
                        label={"show_inactive"}
                        active={showInactive}
                        labelOnRight={true}
                    />
                </div>
            </div>
            <div className="mt-3">
                <OwAccordion
                    title={"accounts_without_tax_documents"}
                    fullWidthClick={true}
                    titleTag={TitleTagEnum.H5}
                    rightElem={<div className={"ms-2"}>({vendorsWithoutTaxDocs.length})</div>}>
                    {vendorsWithoutTaxDocs ? (
                        <div>
                            <OwFnTable columns={tableCols} dataSet={vendorsWithoutTaxDocs} dataFetch={false} initialData={false} />
                        </div>
                    ) : null}
                </OwAccordion>
            </div>

            <div className="mt-3">
                <OwAccordion
                    title={"accounts_with_tax_documents"}
                    fullWidthClick={true}
                    titleTag={TitleTagEnum.H5}
                    rightElem={<div className={"ms-2"}>({vendorsWithTaxDocs.length})</div>}>
                    {vendorsWithTaxDocs ? (
                        <div>{<OwFnTable columns={tableColsTwo} dataSet={vendorsWithTaxDocs} dataFetch={false} initialData={false} />}</div>
                    ) : null}
                </OwAccordion>
            </div>
        </div>
    );
};
